import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import Layout from '../../../layouts';
import Page from '../../../components/Page';
import axios from '../../../utils/axios';
import { fDateSimple } from '../../../utils/formatTime';
import { BotaoExportar, Num, CABECALHO_TABELA_COMPACTO } from '../../../components/gps';
import MenuAcoes from '../../../components/gps/MenuAcoes';

// Ainda em MUI. E o fluxo que TRANSFERE o aparelho - migrar o visual dele e passo
// separado, e nao vale mexer no mesmo commit que troca a tela em volta.
import ModalTransferenciaAutomatica from './components/modal-transferencia-automatica';
import { linhasIniciais, OPCOES_LINHAS, rotuloLinhas } from '../../../components/gps/paginacao';
import TituloTela from '../../../components/gps/TituloTela';

/* ============================================================================
   Transferências - visual novo (shadcn + Tailwind).

   Reescrita no lugar. Contrato, o mesmo de antes:
     GET /dispositivos/todos?page&rows&ordenar=createdAt&direcao=asc&deviceId=..&regional=37

   `regional=37` e fixo (37 = "todas") porque a transferencia precisa achar o
   aparelho em qualquer regional - inclusive numa que o operador nao administra.
   E o mesmo valor que a tela antiga enviava.

   TRES COISAS PRESERVADAS:

   1. SO BUSCA APOS INFORMAR O DEVICE ID. A tela nao lista nada ao abrir - e busca
      por aparelho, um por vez, nao inventario.
   2. A UNICA ACAO DE LINHA E "TRANSFERIR". Na tela antiga o menu vinha com
      `showOnlyTransferenciaAutomatica`, que esconde todas as outras. Mantido: esta
      tela nao e lugar de trocar senha nem apagar aparelho.
   3. As 26 colunas de dado, as mesmas.

   Um detalhe do arquivo antigo que NAO foi copiado: ele importava cinco modais de
   dispositivo (grupo, historico, senha, perda/roubo, grafico) e um
   `handleDeleteRow` com `axios.delete`, todos INALCANCAVEIS - o
   `showOnlyTransferenciaAutomatica` some com os itens de menu que os abririam.
   Eram imports e codigo mortos.
   ============================================================================ */

type Dispositivo = {
  deviceId?: string;
  nome?: string;
  status?: number | string;
  hardwareInfo_manufacturer?: string;
  hardwareInfo_model?: string;
  hardwareInfo_serialNumber?: string;
  softwareInfo_androidVersion?: string;
  networkInfo_imei?: string;
  networkInfo_wifiMacAddress?: string;
  networkInfo_telephonyInfos_phoneNumber_1?: string;
  networkInfo_telephonyInfos_phoneNumber_2?: string;
  networkInfo_telephonyInfos_carrierName_1?: string;
  networkInfo_telephonyInfos_carrierName_2?: string;
  memoryInfo_totalRam?: number | string;
  external_storage_detected?: string;
  cr?: string;
  cpf?: string;
  bateria?: number | string;
  wifi?: string;
  sinal_telefone?: string;
  sinal_dados_moveis?: string;
  consumo_wifi?: string;
  consumo_dados_moveis?: string;
  endereco?: string;
  versaoHub?: string;
  device_log_data?: string;
};


const COLUNAS: Array<{ campo: keyof Dispositivo; rotulo: string; largura: string; mono?: boolean }> = [
  { campo: 'device_log_data', rotulo: 'Última sincronização', largura: '150px', mono: true },
  { campo: 'nome', rotulo: 'Nome', largura: 'minmax(150px,1.4fr)' },
  { campo: 'hardwareInfo_manufacturer', rotulo: 'Marca', largura: '120px' },
  { campo: 'hardwareInfo_model', rotulo: 'Modelo', largura: '140px' },
  { campo: 'bateria', rotulo: 'Bateria', largura: '90px', mono: true },
  { campo: 'hardwareInfo_serialNumber', rotulo: 'Serial Number', largura: '150px', mono: true },
  { campo: 'softwareInfo_androidVersion', rotulo: 'OS', largura: '80px', mono: true },
  { campo: 'sinal_telefone', rotulo: 'Sinal/Telefone', largura: '120px' },
  { campo: 'sinal_dados_moveis', rotulo: 'Sinal/Dados', largura: '110px' },
  { campo: 'wifi', rotulo: 'WIFI', largura: '130px' },
  { campo: 'networkInfo_imei', rotulo: 'IMEI', largura: '150px', mono: true },
  { campo: 'networkInfo_wifiMacAddress', rotulo: 'MAC', largura: '140px', mono: true },
  { campo: 'cr', rotulo: 'CR', largura: '110px' },
  { campo: 'cpf', rotulo: 'CPF', largura: '130px', mono: true },
  { campo: 'endereco', rotulo: 'Endereço', largura: 'minmax(180px,1.6fr)' },
  { campo: 'consumo_wifi', rotulo: 'Consumo/wifi', largura: '120px', mono: true },
  { campo: 'consumo_dados_moveis', rotulo: 'Consumo/dados', largura: '130px', mono: true },
  { campo: 'networkInfo_telephonyInfos_phoneNumber_1', rotulo: 'Telefone 1', largura: '130px', mono: true },
  { campo: 'networkInfo_telephonyInfos_phoneNumber_2', rotulo: 'Telefone 2', largura: '130px', mono: true },
  { campo: 'networkInfo_telephonyInfos_carrierName_1', rotulo: 'Operadora 1', largura: '120px' },
  { campo: 'networkInfo_telephonyInfos_carrierName_2', rotulo: 'Operadora 2', largura: '120px' },
  { campo: 'memoryInfo_totalRam', rotulo: 'Memória RAM', largura: '130px', mono: true },
  { campo: 'external_storage_detected', rotulo: 'Armazenamento', largura: '130px' },
  { campo: 'versaoHub', rotulo: 'Versão do hub', largura: '120px', mono: true },
  { campo: 'status', rotulo: 'Status', largura: '110px' },
];

function texto(v: unknown) {
  const s = v === null || v === undefined || v === '' ? '' : String(v);
  return s || '—';
}

function rotuloStatus(v: unknown) {
  const s = String(v ?? '').toLowerCase();
  if (s === '1' || s === 'ativo') return { rotulo: 'ATIVO', classe: 'bg-ok-soft text-ok' };
  if (s === '2' || s === 'perdido') return { rotulo: 'PERDIDO', classe: 'bg-warn-soft text-warn' };
  if (s === '3' || s === 'pendente') return { rotulo: 'PENDENTE', classe: 'bg-info-soft text-info' };
  return { rotulo: 'INATIVO', classe: 'bg-danger-soft text-danger' };
}

TransferenciasPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default function TransferenciasPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [valor, setValor] = useState('');
  const [pesquisado, setPesquisado] = useState('');
  const [linhas, setLinhas] = useState<Dispositivo[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [linhasPorPagina, setLinhasPorPagina] = useState(linhasIniciais);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  const [menuAberto, setMenuAberto] = useState<string | null>(null);
  const [transferindo, setTransferindo] = useState<Dispositivo | null>(null);

  const buscou = Boolean(pesquisado);

  const carregar = useCallback(async () => {
    if (!pesquisado) return;
    setCarregando(true);
    setErro('');
    try {
      const r = await axios.get<any>(
        `/dispositivos/todos?page=${pagina}&rows=${linhasPorPagina}&ordenar=createdAt&direcao=asc` +
          `&deviceId=${encodeURIComponent(pesquisado)}&regional=37`
      );
      const lista = Array.isArray(r.data) ? r.data : r.data?.content || [];
      setLinhas(lista);
      setTotal(r.data?.totalElements ?? lista.length);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Ops ocorreu um erro ao pesquisar o dispositivo.');
      setLinhas([]);
      setTotal(0);
    } finally {
      setCarregando(false);
    }
  }, [pesquisado, pagina, linhasPorPagina]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  function buscar() {
    const termo = valor.trim();
    if (!termo) {
      enqueueSnackbar('Informe o DEVICE_ID do aparelho.', { variant: 'warning' });
      return;
    }
    // Mesmo termo e mesma pagina nao mudam o estado, entao o efeito nao rodava:
    // depois de um erro (ou de uma transferencia) "Pesquisar" nao fazia nada.
    if (termo === pesquisado && pagina === 0) {
      carregar();
      return;
    }
    setPagina(0);
    setPesquisado(termo);
  }

  function limpar() {
    setValor('');
    setPesquisado('');
    setLinhas([]);
    setTotal(0);
    setPagina(0);
    setErro('');
  }

  const grade = `56px minmax(200px,1.8fr) ${COLUNAS.map((c) => c.largura).join(' ')}`;
  const totalPaginas = Math.max(1, Math.ceil(total / linhasPorPagina));

  return (
    <Page title="Transferências">
      <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2 font-sans text-ink">
        <div className="mb-5">
          <TituloTela
            descricao={
              <>
Busque o aparelho pelo <Num>DEVICE_ID</Num> e use <strong>Transferir</strong> na linha. A busca
            cobre <strong>todas as regionais</strong>, inclusive as que você não administra — é o que permite
            transferir um aparelho que está em outra.
              </>
            }
          >
            Transferências
          </TituloTela>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <input
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && buscar()}
            placeholder="DEVICE_ID"
            className="h-9 w-full max-w-[340px] rounded-lg border border-line bg-surface px-3 tabular-nums text-[12.5px] outline-none placeholder:font-sans placeholder:text-faint focus:border-primary"
          />
          <button
            type="button"
            onClick={buscar}
            className="h-9 rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground"
          >
            Pesquisar
          </button>

          {/* Fica na barra de busca, e nao no cabecalho: aqui a lista SO existe
              depois de uma pesquisa, e o botao ao lado do resultado deixa claro que
              o arquivo e daquele resultado. */}
          <BotaoExportar
            nomeArquivo={`transferencias-${valor || 'busca'}`}
            linhas={() =>
              linhas.map((d) => ({
                'DEVICE_ID': d.deviceId ?? '',
                Nome: d.nome ?? '',
                Marca: d.hardwareInfo_manufacturer ?? '',
                Modelo: d.hardwareInfo_model ?? '',
                'Serial Number': d.hardwareInfo_serialNumber ?? '',
                IMEI: d.networkInfo_imei ?? '',
                CR: d.cr ?? '',
                CPF: d.cpf ?? '',
                Bateria: d.bateria ?? '',
                OS: d.softwareInfo_androidVersion ?? '',
                'Telefone 1': d.networkInfo_telephonyInfos_phoneNumber_1 ?? '',
                'Telefone 2': d.networkInfo_telephonyInfos_phoneNumber_2 ?? '',
                'Operadora 1': d.networkInfo_telephonyInfos_carrierName_1 ?? '',
                'Operadora 2': d.networkInfo_telephonyInfos_carrierName_2 ?? '',
              }))
            }
          />
          <button
            type="button"
            onClick={limpar}
            className="h-9 rounded-lg border border-line px-3 text-[12px] font-semibold text-muted-foreground hover:text-ink"
          >
            Limpar
          </button>
        </div>

        {!buscou ? (
          <div className="rounded-lg border border-line bg-surface p-10 text-center shadow-card">
            <p className="text-[12.5px] text-muted-foreground">
              Informe o <Num>DEVICE_ID</Num> do aparelho e clique em <strong>Pesquisar</strong>.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-primary/25 bg-surface shadow-card">
            <div className="overflow-x-auto">
              <div style={{ minWidth: 56 + 200 + COLUNAS.length * 130 }}>
                <div
                  className={CABECALHO_TABELA_COMPACTO}
                  style={{ gridTemplateColumns: grade }}
                >
                  <div className="text-center">Opções</div>
                  <div>Device ID</div>
                  {COLUNAS.map((c) => (
                    <div key={String(c.campo)} className="min-w-0 pr-2">
                      <span className="block truncate">{c.rotulo}</span>
                    </div>
                  ))}
                </div>

                <div className="max-h-[58vh] overflow-y-auto">
                  {carregando && (
                    <div className="p-8 text-center text-[12.5px] text-muted-foreground">Pesquisando…</div>
                  )}

                  {!carregando && erro && <div className="p-8 text-center text-[12.5px] text-danger">{erro}</div>}

                  {!carregando && !erro && !linhas.length && (
                    <div className="p-8 text-center text-[12.5px] text-muted-foreground">
                      Nenhum dispositivo encontrado com esse DEVICE_ID.
                    </div>
                  )}

                  {!carregando &&
                    !erro &&
                    linhas.map((d, i) => (
                      <div
                        key={d.deviceId || `sem-device-${i}`}
                        className="grid items-center border-b px-3 py-1.5 hover:bg-surface-2"
                        style={{ gridTemplateColumns: grade, borderColor: 'var(--gps-soft)' }}
                      >
                        <div className="flex justify-center">
                          {/* Somente "Transferir" - a tela antiga usava
                              `showOnlyTransferenciaAutomatica`, que esconde as
                              demais acoes. Esta tela nao e lugar de trocar senha
                              nem apagar aparelho. */}
                          <MenuAcoes<Dispositivo>
                            linha={d}
                            aberto={menuAberto === d.deviceId}
                            onAlternar={() => setMenuAberto(menuAberto === d.deviceId ? null : d.deviceId ?? null)}
                            onFechar={() => setMenuAberto(null)}
                            acoes={[{ rotulo: 'Transferir', ao: (x) => setTransferindo(x) }]}
                          />
                        </div>

                        <div className="min-w-0 pr-2">
                          <div className="flex min-w-0 items-center gap-2">
                            <Num className="block truncate text-[11px] font-semibold">{texto(d.deviceId)}</Num>
                          </div>
                        </div>

                        {COLUNAS.map((c) => {
                          const bruto = (d as any)[c.campo];

                          if (c.campo === 'status') {
                            const s = rotuloStatus(bruto);
                            return (
                              <div key="status" className="min-w-0 pr-2">
                                <span
                                  className={`inline-flex h-[22px] items-center gap-1.5 rounded-md px-2 text-[11px] font-bold tracking-wide ${s.classe}`}
                                >
                                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                                  {s.rotulo}
                                </span>
                              </div>
                            );
                          }

                          let valorCelula = texto(bruto);
                          let classe = 'text-muted-foreground';
                          if (c.campo === 'device_log_data') valorCelula = fDateSimple(d.device_log_data) || '—';
                          if (c.campo === 'bateria' && bruto !== null && bruto !== undefined && bruto !== '') {
                            valorCelula = `${bruto}%`;
                            if (Number(bruto) <= 20) classe = 'font-bold text-danger';
                          }

                          return (
                            <div key={String(c.campo)} className="min-w-0 pr-2">
                              {c.mono ? (
                                <Num className={`block truncate text-[11px] ${classe}`}>{valorCelula}</Num>
                              ) : (
                                <span className={`block truncate text-[12px] ${classe}`} title={valorCelula}>
                                  {valorCelula}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-3.5 py-2.5 pr-16 text-[12px] text-muted-foreground">
              <span className="flex items-center gap-2">
                <span>
                  Mostrando <Num>{linhas.length}</Num> de <Num>{total.toLocaleString('pt-BR')}</Num>
                </span>
                <label className="flex items-center gap-1.5">
                  <span className="text-faint">por página</span>
                  <select
                    value={linhasPorPagina}
                    onChange={(e) => {
                      setPagina(0);
                      setLinhasPorPagina(Number(e.target.value));
                    }}
                    className="h-7 rounded-md border border-line bg-surface px-1.5 text-[12px] tabular-nums outline-none focus:border-primary"
                  >
                    {OPCOES_LINHAS.map((n) => (
                      <option key={n} value={n}>
                        {rotuloLinhas(n)}
                      </option>
                    ))}
                  </select>
                </label>
              </span>

              <span className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={pagina === 0}
                  onClick={() => setPagina((p) => Math.max(0, p - 1))}
                  className="h-7 rounded-md border border-line px-2 font-semibold disabled:opacity-40"
                >
                  Anterior
                </button>
                <span>
                  <Num>{pagina + 1}</Num> / <Num>{totalPaginas}</Num>
                </span>
                <button
                  type="button"
                  disabled={pagina + 1 >= totalPaginas}
                  onClick={() => setPagina((p) => p + 1)}
                  className="h-7 rounded-md border border-line px-2 font-semibold disabled:opacity-40"
                >
                  Próxima
                </button>
              </span>
            </div>
          </div>
        )}
      </div>

      {transferindo?.deviceId && (
        <ModalTransferenciaAutomatica
          open={Boolean(transferindo)}
          setOpen={(v: boolean) => !v && setTransferindo(null)}
          device={transferindo as any}
        />
      )}
    </Page>
  );
}
